import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import LoadingBackdrop from '@/components/Grid/LoadingBackdrop';
import SearchInput from '@/components/SearchInput';
import IOSSwitch from '@/components/Switch';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAdmin, selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { setFilters } from '@/store/slices/segments.slice';
import { Add, FileDownloadOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import {
  GridApiPro,
  GridCsvGetRowsToExportParams,
  GridRowId,
  GridToolbarContainer,
  gridFilteredSortedRowIdsSelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { ChangeEvent, useCallback, useState } from 'react';
import FilterPopover from './FilterPopover';

const getSelectedRowsToExport = ({ apiRef }: GridCsvGetRowsToExportParams<GridApiPro>): GridRowId[] => {
  return gridFilteredSortedRowIdsSelector(apiRef);
};

interface ISegmentToolBarProps {
  setQuickFilter: (filter: string[]) => void;
  onCreateNewClick: () => void;
  active: boolean;
  loading?: boolean;
}

const ToolBar = ({ setQuickFilter, onCreateNewClick, active, loading }: ISegmentToolBarProps) => {
  const apiRef = useGridApiContext();

  const dispatch = useAppDispatch();
  const [filterValue, setFilterValue] = useState<string>('');

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFilterValue(value);
      setQuickFilter(value.trim().split(' ') || []);
    },
    [setQuickFilter],
  );

  const onActiveChange = useCallback(() => {
    dispatch(setFilters({ active: !active }));
  }, [dispatch, active]);

  const onGenerateReportClick = useCallback(() => {
    apiRef.current.exportDataAsCsv({
      //@ts-expect-error pro and community version mismatch
      getRowsToExport: getSelectedRowsToExport,
      fileName: `Segments_${Date.now()}`,
    });
  }, [apiRef]);

  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.SEGMENTS));
  const isAdmin = useAppSelector(selectIsAdmin);

  return (
    <>
      <GridToolbarContainer sx={{ padding: 0 }}>
        <div className='py-[24px] px-[30px] flex items-center justify-between relative w-full'>
          <div className='flex gap-[12px]'>
            <SearchInput placeholder='Search..' value={filterValue} onChange={onInputChange} />
            {isAdmin && (
              <Button variant='outlined' onClick={onActiveChange} sx={{ height: '37px' }}>
                <span>Archive</span>
                <IOSSwitch sx={{ marginLeft: '7px' }} checked={!active} />
              </Button>
            )}
            <FilterPopover />
          </div>
          <div className='flex gap-[8px]'>
            <Button variant='outlined' onClick={onGenerateReportClick} disabled={loading}>
              <FileDownloadOutlined sx={{ width: 20, height: 20, marginRight: '4px' }} />
              <span>Download Table</span>
            </Button>
            <Button variant='contained' onClick={onCreateNewClick} disabled={readOnly}>
              <Add sx={{ width: 16, height: 16, marginRight: '2px' }} />
              <span>Create New</span>
            </Button>
          </div>
        </div>
        {loading && <LoadingBackdrop />}
      </GridToolbarContainer>
    </>
  );
};

export default ToolBar;
